// FROM: https://github.com/modusign/modusign-client/blob/b6c3d577fdd8384ea2f73c5ab605ead1afc782d1/app/src/components/ParticipantFieldStatusBoard/index.tsx

import {
  faBuilding,
  faCalendar,
  faCalendarStar,
  faHouseBlank,
  faList,
  faPaperclipVertical,
  faSquareCheck,
  faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { faRotate } from '@fortawesome/pro-solid-svg-icons';
import { Box, Flex, Text } from '@modusign/design-system';
import { useAttachments, useFields, useSigner } from '@modusign/react-signing';
import { useUIState } from '@modusign/react-signing/setup';
import {
  AutofillFieldSupportFieldType,
  CheckboxFieldGroup,
  FieldNode,
  FieldNodeType,
  SignatureField,
  SignatureType,
} from '@modusign/react-signing/types';
import { produce } from 'immer';
import groupBy from 'lodash/groupBy';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useToggle } from 'react-use';

import { TextFieldIcon } from '@app/components/Icons';
import SignerFieldFontAwesomeIcon from '@app/components/Icons/SignerFieldFontAwesomeIcon';
import MaxAttachmentAlertModal from '@app/components/Modal/MaxAttachmentAlertModal';
import AllowSignatureTypeSelectionConfirmModal from '@app/components/ParticipantFieldStatusBoard/AllowSignatureTypeSelectionConfirmModal';
import SetupButton from '@app/components/SetupButton';
import SetupButtonDropdown from '@app/components/SetupButtonDropdown';
import AttachmentExceededAlertModal from '@app/containers/Modal/AttachmentExceededAlertModal';
import { Viewport } from '@app/features/apply-react-signing/components/FieldRenderer/types';
import FieldLocatorSignature from '@app/features/apply-react-signing/components/FieldSetupStep/FieldLoactorSignature';
import AddAttachFileRequestInfo from '@app/features/apply-react-signing/components/FieldSetupStep/ParticipantFieldStatusBoard/AddAttachFileRequestInfo';
import AttachmentSettingModal, {
  DOCUMENT_MAX_ATTACHMENTS_COUNT,
} from '@app/features/apply-react-signing/components/FieldSetupStep/ParticipantFieldStatusBoard/AttachmentSettingModal';
import { getPageInField } from '@app/features/apply-react-signing/libs/getFiledNodePage';
import { useSignatureFieldSize } from '@app/features/apply-react-signing/libs/hooks/useSignatureFieldSize';
import useUsageCount from '@app/hooks/useUsageCount';
import createOrdinalCharacter, { AllowedOrdinalNumbers } from '@app/libs/ordinalCharacter';
import orderRotationForColor from '@app/libs/participantFields/orderRotationForColor';
import { scaleSelector } from '@app/libs/reactPdfJs/store/selectors';
import { ModalType } from '@app/store/modal/types';
import getIsDataLabelEditable from '@app/store/selectors/getIsDataLabelEditable';

export type SupportFieldType = Exclude<FieldNodeType, 'IMAGE' | 'AUTOFILL_GROUP'>;

interface ParticipantFieldStatusBoardProps {
  index: number;
  supportedFields: SupportFieldType[];
  isSigningInOrder: boolean | null;
  isTemplateSetting: boolean;
  needAuthorityCheck: boolean;
  order: number;
  signerId: string;
  readOnly?: boolean;
  viewports: Viewport[];
  onModalOpen?: (modalType: ModalType, props?: { content?: string; title?: string }) => void;
}

const SIGNATURE_FIELDS_LIMIT = 100;

function ParticipantFieldStatusBoard({
  viewports,
  index,
  isSigningInOrder,
  isTemplateSetting,
  needAuthorityCheck,
  supportedFields,
  order,
  signerId,
  readOnly,
  onModalOpen = () => {},
}: ParticipantFieldStatusBoardProps) {
  const { attachments } = useAttachments();
  const { attachmentRequestCount } = useUsageCount();

  const attachmentTotalCount = attachments.length;
  const isDataLabelEditable = useSelector(getIsDataLabelEditable);
  const scale = useSelector(scaleSelector);

  const { signer } = useSigner(signerId);
  const { fields, changeFields } = useFields();
  const { changeAllow } = useSignatureFieldSize();
  const { ui, setInsertingField } = useUIState();
  const ownFields = fields.filter((f) => f.participantReference.id === signerId);
  const ownAttachments = attachments.filter((f) => f.participantReference.id === signerId);

  const lastDisableSignatureGenerator = useLastDisabledSignatureGeneratorConfiguration(signerId);

  const [isSignatureSelectionConfirmationModalOpen, setIsSignatureSelectionConfirmationModalOpen] =
    useState(false);
  const [isAttachmentSettingModalOpen, setIsAttachmentSettingModalOpen] = useState(false);
  const [isAttachmentExceededAlertModalOpen, toggleAttachmentExceededAlertModalOpen] =
    useToggle(false);
  const [isMaxAttachmentAlertModalOpen, toggleMaxAttachmentAlertModalOpen] = useToggle(false);

  const [selectedAllowSignatureTypes, setSelectedAllowSignatureTypes] = useState<SignatureType[]>(
    () => {
      const signitureField = ownFields.find(
        (field): field is SignatureField => field.type === 'SIGNATURE'
      );
      return signitureField?.config.allow ?? ['SIGN', 'STAMP'];
    }
  );
  const [tempAllowSignatureTypesForConfirmModal, setTempAllowSignatureTypesForConfirmModal] =
    useState<SignatureType[]>(['SIGN', 'STAMP']);

  const selectedAllowSignatureTypesText = useMemo(() => {
    if (
      tempAllowSignatureTypesForConfirmModal.includes('SIGN') &&
      tempAllowSignatureTypesForConfirmModal.includes('STAMP')
    ) {
      return '사인/도장';
    }

    if (tempAllowSignatureTypesForConfirmModal.includes('SIGN')) {
      return '사인';
    }

    if (tempAllowSignatureTypesForConfirmModal.includes('STAMP')) {
      return '도장';
    }

    return '사인/도장';
  }, [tempAllowSignatureTypesForConfirmModal]);

  const [selectedSyncGroupFieldType, setSelectedSyncGroupField] = useState<null | Extract<
    FieldNodeType,
    AutofillFieldSupportFieldType
  >>(null);

  const fieldCountMap = useMemo(() => {
    // TODO (@DevSDK): 아래 로직 개선하기:
    // https://github.com/modusign/modusign-client/pull/10084#discussion_r1375838900
    const countTarget = ownFields.flatMap((f) => {
      if (f.type === 'AUTOFILL_GROUP') {
        return f.children;
      }
      return f;
    });
    const group = groupBy(countTarget, (field) => field.type);
    return new Map(Object.entries(group).map(([key, value]) => [key, value.length]));
  }, [ownFields]);

  const signatureFieldCount = fieldCountMap.get('SIGNATURE') ?? 0;
  const textFieldCount = fieldCountMap.get('TEXT') ?? 0;
  const dropdownFieldCount = fieldCountMap.get('DROPDOWN') ?? 0;
  const nameFieldCount = fieldCountMap.get('NAME') ?? 0;
  const companyNameFieldCount = fieldCountMap.get('COMPANY_NAME') ?? 0;
  const addressFieldCount = fieldCountMap.get('ADDRESS') ?? 0;
  const dateFieldCount = fieldCountMap.get('DATE') ?? 0;
  const signingDateFieldCount = fieldCountMap.get('SIGNING_DATE') ?? 0;

  const checkboxGroupCount = useMemo(() => {
    return ownFields
      .filter((field): field is CheckboxFieldGroup => field.type === 'CHECKBOX_GROUP')
      .reduce((acc, field) => acc + field.children.length, 0);
  }, [ownFields]);

  const getInsertingFieldPage = useCallback(
    (type: FieldNodeType) => {
      const lastModified = ui.lastInsertedField.SIGNER[type];
      if (!lastModified) {
        return 1;
      }
      return getPageInField(lastModified);
    },
    [ui.lastInsertedField.SIGNER]
  );

  const getInsertingFieldViewPort = useCallback(
    (type: FieldNodeType) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return viewports.find((viewport) => viewport.pageNumber === getInsertingFieldPage(type))!;
    },
    [getInsertingFieldPage, viewports]
  );

  function updateInsertingSignatureField(config: {
    allow: SignatureType[];
    disableSignatureGenerator?: boolean;
  }) {
    setInsertingField({
      type: 'SIGNATURE',
      viewport: getInsertingFieldViewPort('SIGNATURE'),
      pdfScale: scale,
      participantId: signerId,
      page: getInsertingFieldPage('SIGNATURE'),
      config,
    });
  }

  function handleFieldCreationClicked(type: FieldNodeType) {
    if (SIGNATURE_FIELDS_LIMIT <= signatureFieldCount && type === 'SIGNATURE') {
      onModalOpen('CONFIRM', {
        title: '사인/도장 설정 개수 초과',
        content: `사인/도장 입력란은 최대 ${SIGNATURE_FIELDS_LIMIT}개까지\n 설정할 수 있습니다.`,
      });
      return;
    }

    if (type === 'SIGNATURE') {
      setInsertingField({
        type,
        viewport: getInsertingFieldViewPort(type),
        pdfScale: scale,
        participantId: signerId,
        page: getInsertingFieldPage(type),
        config: {
          allow: selectedAllowSignatureTypes,
          // 추후 react-signing 내부로 로직을 옮겨야합니다 https://github.com/modusign/modusign-client/pull/10595
          disableSignatureGenerator: Object.keys(lastDisableSignatureGenerator).length > 0,
        },
      });
    } else {
      setInsertingField({
        type,
        viewport: getInsertingFieldViewPort(type),
        pdfScale: scale,
        participantId: signerId,
        page: getInsertingFieldPage(type),
      });
    }
  }

  function handleSyncGroupCreate(type: typeof selectedSyncGroupFieldType) {
    if (type !== null) {
      setInsertingField({
        type,
        viewport: getInsertingFieldViewPort(type),
        pdfScale: scale,
        participantId: signerId,
        page: getInsertingFieldPage(type),
      });
    }
  }

  const handleAttachmentAdd = useCallback(() => {
    if (!readOnly) {
      if (needAuthorityCheck) {
        if (attachmentRequestCount.limit <= attachmentTotalCount && ownAttachments.length === 0) {
          toggleAttachmentExceededAlertModalOpen();
          return;
        }
      }

      if (DOCUMENT_MAX_ATTACHMENTS_COUNT <= attachmentTotalCount && ownAttachments.length === 0) {
        toggleMaxAttachmentAlertModalOpen();
        return;
      }

      setIsAttachmentSettingModalOpen(true);
    }
  }, [
    readOnly,
    needAuthorityCheck,
    attachmentTotalCount,
    ownAttachments.length,
    attachmentRequestCount.limit,
    toggleAttachmentExceededAlertModalOpen,
    toggleMaxAttachmentAlertModalOpen,
  ]);

  function handleSelectAllowSignatureTypes(selectedAllowSignatureTypesValue: SignatureType[]) {
    if (
      JSON.stringify(selectedAllowSignatureTypesValue) !==
      JSON.stringify(selectedAllowSignatureTypes)
    ) {
      if (signatureFieldCount === 0) {
        setSelectedAllowSignatureTypes(selectedAllowSignatureTypesValue);
        updateInsertingSignatureField({
          allow: selectedAllowSignatureTypesValue,
          disableSignatureGenerator: isEqual(selectedAllowSignatureTypesValue, ['STAMP'])
            ? lastDisableSignatureGenerator[signerId]
            : false,
        });
      }

      if (signatureFieldCount > 0) {
        setTempAllowSignatureTypesForConfirmModal(selectedAllowSignatureTypesValue);
        setIsSignatureSelectionConfirmationModalOpen(true);
      }
    }
  }

  function handleSelectAllowSignatureTypesConfirm() {
    setIsSignatureSelectionConfirmationModalOpen(false);
    setSelectedAllowSignatureTypes(tempAllowSignatureTypesForConfirmModal);

    changeAllow(tempAllowSignatureTypesForConfirmModal, signerId);

    changeFields((fields) => {
      return fields.map(
        produce((draft: FieldNode) => {
          if (draft.participantReference.id === signerId && draft.type === 'SIGNATURE') {
            if (draft.config.allow.includes('SIGN')) {
              draft.config.disableSignatureGenerator = false;
            } else {
              draft.config.disableSignatureGenerator = lastDisableSignatureGenerator[signerId];
            }
          }
        })
      ) as FieldNode[];
    });

    updateInsertingSignatureField({
      allow: tempAllowSignatureTypesForConfirmModal,
      disableSignatureGenerator: isEqual(tempAllowSignatureTypesForConfirmModal, ['STAMP'])
        ? lastDisableSignatureGenerator[signerId]
        : false,
    });
  }

  const signerName = (isTemplateSetting ? signer?.role : signer?.name) || '';

  return (
    <>
      <Box
        aria-label="participant field status board"
        backgroundColor="cardBackgroundNeutralBase"
        borderColor="dividerNeutralBase"
        borderRadius="6px"
        borderStyle="solid"
        borderWidth="1px"
        mb={5}
        position="relative"
        px={4}
        py={6}
        sx={{
          button: {
            userSelect: 'none',
          },
          '&::before': {
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
            content: "' '",
            display: 'block',
            height: '4px',
            left: 0,
            position: 'absolute',
            top: 0,
            width: '100%',
            backgroundColor: `participantOrder${orderRotationForColor(index + 1)}`,
          },
        }}
        width="100%"
      >
        <Box mb={1}>
          <Text fontWeight="medium">
            {isSigningInOrder && createOrdinalCharacter(order as AllowedOrdinalNumbers)} 서명자
          </Text>
          {signer?.role && (
            <Text color="textEmphasisBase" fontWeight="medium" ml={1}>
              {signer.role}
            </Text>
          )}
        </Box>
        <Box mb={5}>
          {signer?.name && (
            <Text color="textNeutralBase" sx={{ wordBreak: 'break-all' }}>
              {signer.name}
            </Text>
          )}
        </Box>
        <Box mb={5}>
          {supportedFields.includes('SIGNATURE') && (
            <FieldLocatorSignature
              allowSignatureTypes={selectedAllowSignatureTypes}
              count={signatureFieldCount}
              order={order}
              readOnly={readOnly || false}
              onClick={() => handleFieldCreationClicked('SIGNATURE')}
              onSelectAllowSignatureType={handleSelectAllowSignatureTypes}
            />
          )}
          {supportedFields.includes('TEXT') && (
            <SetupButton
              count={textFieldCount}
              leftIcon={<TextFieldIcon order={order} />}
              readOnly={readOnly}
              testId="participant-field-text"
              text="텍스트"
              onClick={() => handleFieldCreationClicked('TEXT')}
            >
              서명자가 문서에 내용을 입력해야 할 위치에 놓아주세요.
            </SetupButton>
          )}
          {supportedFields.includes('CHECKBOX_GROUP') && (
            <SetupButton
              count={checkboxGroupCount}
              leftIcon={<SignerFieldFontAwesomeIcon icon={faSquareCheck} order={order} />}
              readOnly={readOnly}
              testId="participant-field-checkbox"
              text="체크박스"
              onClick={() => handleFieldCreationClicked('CHECKBOX_GROUP')}
            >
              서명자가 체크해야 할 위치에 놓아주세요.
            </SetupButton>
          )}

          {supportedFields.includes('SIGNING_DATE') && (
            <SetupButton
              count={signingDateFieldCount}
              leftIcon={<SignerFieldFontAwesomeIcon icon={faCalendarStar} order={order} />}
              readOnly={readOnly}
              testId="participant-field-signing-date"
              text="서명한 날짜"
              onClick={() => handleFieldCreationClicked('SIGNING_DATE')}
            >
              서명한 날짜가 <br />
              자동으로 입력됩니다.
            </SetupButton>
          )}
          {!readOnly && (
            <Box mt={3}>
              <Flex alignItems="center" height="100%" justifyContent="flex-start" pb={4} pt={3}>
                <Text color="textNeutralLight" fontWeight="medium">
                  고급 입력란
                </Text>
                {order === 1 && (
                  <Flex
                    alignItems="center"
                    bg="iconPrimaryLight"
                    borderRadius="3px"
                    color="textNeutralBase"
                    display="inline-flex"
                    minWidth="max-content"
                    ml={2}
                    px={2}
                    py={1}
                    textAlign="center"
                  >
                    <Text fontSize="9px" fontWeight="bold">
                      BETA
                    </Text>
                  </Flex>
                )}
              </Flex>
            </Box>
          )}
          {supportedFields.includes('DATE') && (
            <Box>
              <SetupButton
                count={dateFieldCount}
                leftIcon={<SignerFieldFontAwesomeIcon icon={faCalendar} order={order} />}
                readOnly={readOnly}
                testId="participant-field-date"
                text="날짜"
                onClick={() => handleFieldCreationClicked('DATE')}
              >
                날짜를 정해진 형식으로 <br />
                입력하게 할 수 있습니다.
              </SetupButton>
            </Box>
          )}
          {supportedFields.includes('DROPDOWN') && (
            <Box>
              <SetupButton
                count={dropdownFieldCount}
                leftIcon={<SignerFieldFontAwesomeIcon icon={faList} order={order} />}
                readOnly={readOnly}
                testId="participant-field-dropdown"
                text="드롭다운"
                onClick={() => handleFieldCreationClicked('DROPDOWN')}
              >
                서명자가 문서에서 옵션을 선택해야 할 위치에 놓아주세요.
              </SetupButton>
            </Box>
          )}
          <SetupButtonDropdown
            items={[
              {
                type: 'NAME' as const,
                icon: <SignerFieldFontAwesomeIcon icon={faUser} order={order} />,
                text: '서명자 이름',
                count: nameFieldCount,
              },
              {
                type: 'COMPANY_NAME' as const,
                icon: <SignerFieldFontAwesomeIcon icon={faBuilding} order={order} />,
                text: '회사 이름',
                count: companyNameFieldCount,
              },
              {
                type: 'ADDRESS' as const,
                icon: <SignerFieldFontAwesomeIcon icon={faHouseBlank} order={order} />,
                text: '주소',
                count: addressFieldCount,
              },
            ].filter((f) => supportedFields.includes(f.type))}
            placeholder="자동 입력"
            placeholderIcon={<SignerFieldFontAwesomeIcon icon={faRotate} order={order} />}
            readOnly={readOnly}
            tooltip={
              <Flex flexDirection="column">
                <Text>서명자가 한 번만 입력해도 다른 입력란에</Text>
                <Text>동일한 정보가 입력됩니다.</Text>
              </Flex>
            }
            type={selectedSyncGroupFieldType}
            onClick={() => handleSyncGroupCreate(selectedSyncGroupFieldType)}
            onSelect={(type) => {
              setSelectedSyncGroupField(type);
              if (type !== null) {
                handleSyncGroupCreate(type);
              }
            }}
          />
        </Box>
        {readOnly ? (
          <AddAttachFileRequestInfo attachments={ownAttachments} />
        ) : (
          <SetupButton
            count={ownAttachments.length}
            leftIcon={<SignerFieldFontAwesomeIcon icon={faPaperclipVertical} order={order} />}
            readOnly={readOnly}
            testId="participant-file-attachment"
            text="첨부파일 요청"
            onClick={handleAttachmentAdd}
          />
        )}
      </Box>
      {isSignatureSelectionConfirmationModalOpen && (
        <AllowSignatureTypeSelectionConfirmModal
          allowSignatureTypesText={selectedAllowSignatureTypesText}
          isOpen
          participantName={signerName}
          onCancel={() => setIsSignatureSelectionConfirmationModalOpen(false)}
          onConfirm={handleSelectAllowSignatureTypesConfirm}
        />
      )}
      {isAttachmentExceededAlertModalOpen && (
        <AttachmentExceededAlertModal onConfirm={toggleAttachmentExceededAlertModalOpen} />
      )}
      {isMaxAttachmentAlertModalOpen && (
        <MaxAttachmentAlertModal
          count={DOCUMENT_MAX_ATTACHMENTS_COUNT}
          onConfirm={() => toggleMaxAttachmentAlertModalOpen()}
        />
      )}
      {isAttachmentSettingModalOpen && (
        <AttachmentSettingModal
          isOpen
          needAuthorityCheck={needAuthorityCheck}
          showDataLabelInput={isDataLabelEditable && isTemplateSetting}
          signerAttachments={ownAttachments}
          signerId={signerId}
          signerName={signerName}
          onModalClose={() => setIsAttachmentSettingModalOpen(false)}
        />
      )}
    </>
  );
}

/**
 * 가장 마지막으로 설정된 SIGNATURE/allow:[STAMP] 필드의 disableSignatureGenerator 값을 저장하는 훅
 * FIXME: react-signing 으로 이동해야 함. allow와 상관없이 disableSignatureGenerator가 사용될 때, 리팩토링 필요
 */
function useLastDisabledSignatureGeneratorConfiguration(signerId: string) {
  const [lastDisableSignatureGenerator, setLastDisableSignatureGenerator] = useState<
    Record<string, boolean>
  >({});

  const { fields } = useFields();

  useEffect(() => {
    const field = fields.find(
      (f): f is SignatureField =>
        f.participantReference.id === signerId &&
        f.type === 'SIGNATURE' &&
        isEqual(f.config.allow, ['STAMP'])
    );
    if (field) {
      setLastDisableSignatureGenerator({ [signerId]: field.config.disableSignatureGenerator });
    }
  }, [fields, signerId]);

  return lastDisableSignatureGenerator;
}

export default ParticipantFieldStatusBoard;
